import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch('https://readdy.ai/api/form/d7cm229noju8nk5a0uh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubStatus('success');
        setEmail('');
      } else {
        setSubStatus('error');
      }
    } catch {
      setSubStatus('error');
    }
  };

  return (
    <footer className="bg-[#0a0a0a] pt-20 pb-0">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 pb-16 border-b border-[#d4af37]/15">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="https://public.readdy.ai/ai/img_res/a0e0e895-3f57-4fcf-89e3-e09f29edb039.png"
              alt="Advantixx Logo"
              className="h-10 w-auto object-contain mb-4"
            />
            {/* ACHC Certified Consultant badge — co-branded per ACHC brand guidelines */}
            <a
              href="https://www.achc.org/certified-consultants.html"
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-block mb-5 cursor-pointer hover:opacity-80 transition-opacity duration-200"
              title="ACHC Certified Consultant — Click to verify on ACHC.org"
            >
              <img
                src="https://storage.readdy-site.link/project_files/08c9742b-b998-4dd6-add2-07c3775679f4/889fdc5b-3fe1-4cf9-8f22-80529d37f789_ACHC-Certified-Consultant-Badge.png?v=954a2225981db5d3506bfc978a15e293"
                alt="ACHC Certified Consultant Badge"
                className="h-16 w-auto object-contain"
              />
            </a>
            <p className="text-[#888] text-sm leading-relaxed">
              Premier ACHC accreditation consulting for home health agencies. We simplify compliance and position your agency for long-term success.
            </p>
            <div className="flex items-center gap-4 mt-6">
              {['ri-linkedin-fill', 'ri-facebook-fill', 'ri-twitter-x-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0d0d0d] transition-all duration-200 cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="lg:col-span-1">
            <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Stay Updated</p>
            <p className="text-[#888] text-sm mb-4 leading-relaxed">Get the latest ACHC compliance tips and industry updates.</p>
            <form
              data-readdy-form
              onSubmit={handleNewsletter}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center border-b border-[#d4af37]/40 pb-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 bg-transparent text-sm text-[#ccc] placeholder-[#555] outline-none"
                  required
                />
                <button
                  type="submit"
                  className="w-7 h-7 flex items-center justify-center text-[#d4af37] hover:text-[#f4c430] cursor-pointer"
                >
                  <i className="ri-arrow-right-line text-lg"></i>
                </button>
              </div>
              {subStatus === 'success' && (
                <p className="text-green-400 text-xs">Subscribed successfully!</p>
              )}
              {subStatus === 'error' && (
                <p className="text-red-400 text-xs">Something went wrong. Try again.</p>
              )}
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Quick Links</p>
            <ul className="flex flex-col gap-3">
              {[
                { label: 'Home', path: '/' },
                { label: 'About Us', path: '/about' },
                { label: 'Services', path: '/services' },
                { label: 'Why Dr. Villarreal', path: '/why-dr-villarreal' },
                { label: 'Blog', path: '/blog' },
                { label: 'FAQ', path: '/faq' },
                { label: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#888] text-sm hover:text-[#d4af37] transition-colors duration-200 cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[#d4af37] text-xs uppercase tracking-widest font-semibold mb-5">Contact</p>
            <ul className="flex flex-col gap-4">
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-[#d4af37] text-sm"></i>
                </div>
                <span className="text-[#888] text-sm">info@advantixx.com</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-[#d4af37] text-sm"></i>
                </div>
                <span className="text-[#888] text-sm">+1 (800) 555-0199</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-[#d4af37] text-sm"></i>
                </div>
                <span className="text-[#888] text-sm">United States — Nationwide Service</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[#555] text-xs">
            &copy; {new Date().getFullYear()} Advantixx Home Health Accreditation Consultants. All rights reserved.
          </p>
          <p className="text-[#555] text-xs">
            ACHC Certified Consulting &bull; Nationwide
          </p>
        </div>

        {/* Large brand watermark */}
        <div className="overflow-hidden pb-0">
          <p
            className="text-[#e8dcc4]/8 font-black tracking-tighter select-none leading-none"
            style={{ fontSize: 'clamp(60px, 12vw, 140px)' }}
          >
            ADVANTIXX
          </p>
        </div>
      </div>
    </footer>
  );
}
